import {Text} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import classNames from 'classnames';
import {parseCookies} from 'nookies';
import type {FC} from 'react';
import {MdWork} from 'react-icons/md';
import type {z} from 'zod';
import {ReportTagType} from '../../../../../core/entities/reports/constants';
import type {ReportSchema} from '../../../../../core/entities/reports/schema';
import {isInternReport} from '../../../../../core/entities/shared/isInternReport';
import {convertStringToReportType} from '../../../../../core/factories/reports/convertStringToReportType';
import {validateCookie} from '../../../../../core/usecases/auth/checkOauthTokenExistence';
import {ClipButtonTypeA} from '../../../../../shared/components/ClipButton/TypeA';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {NoLoginAlertModal} from '../../../../../shared/components/NoLoginAlertModal';
import {convertGraduationYearToLabel} from '../../../../../shared/services/convertGraduatioinYearToLabel';
import {formatDate} from '../../../../../shared/services/formatDate';
import {useMediaQuery} from '../../../../../shared/services/useMediaQuery';
import {useHandleClip} from '../../../usecase/useHandleClip';
import {ReportTypeLabel} from './ReportTypeLabel';

type Props = {
  report: z.infer<typeof ReportSchema>;
};

export const ReportCard: FC<Props> = ({report}) => {
  const cookies = parseCookies();
  const {isValid} = validateCookie(cookies);

  const {isPC} = useMediaQuery();

  const reportUrl =
    report.type === 'entrysheet'
      ? `/passed_es/${report.id}`
      : `/selection_reports/${report.id}`;

  const [opened, {open, close}] = useDisclosure(false);

  const {handleClip, clipCountUpdated, isClipped} = useHandleClip({
    isLogin: isValid,
    reportId: report.id,
    reportType: report.type,
    clipCount: report.clipsCount,
    clipped: report.clipped,
  });

  const handleClipClick = async () => {
    if (isValid) {
      return handleClip();
    }

    open();
  };

  const offerStatusLabel = () => {
    if (isInternReport(report.type)) {
      return ReportTagType.INTERN;
    }

    if (report.reporterGetOffer) {
      return ReportTagType.OFFERED;
    }

    return ReportTagType.REJECTED;
  };

  return (
    <>
      <CommonLink
        href={reportUrl}
        className="flex cursor-pointer flex-col shadow-card bg-white rounded-[20px] p-16 block"
        style={{textDecoration: 'none', color: 'inherit'}}
      >
        <div
          className={classNames('w-full flex flex-col', {
            'p-16 h-[105px]': isPC,
            'p-4': !isPC,
          })}
        >
          <div className="flex flex-row justify-between items-start">
            <div className="flex flex-row items-center gap-x-4">
              <ReportTypeLabel type={convertStringToReportType(report.type)} />
              <div className="py-2 flex items-center justify-center rounded-full border-[1px] border-line px-8 text-sm text-black-2">
                {convertGraduationYearToLabel(report.reporterGraduationYear)}
              </div>
              <div className="py-2 flex items-center justify-center rounded-full border-[1px] border-line px-8 text-sm text-black-2">
                {offerStatusLabel()}
              </div>
              {report.isNew && <Text className="text-sm text-sub">NEW</Text>}
            </div>
            <div className="flex flex-row items-center gap-x-8">
              <ClipButtonTypeA
                count={`${clipCountUpdated}`}
                isClipped={isClipped}
                handleUpdate={async () => handleClipClick()}
              />
            </div>
          </div>
          <div className=" flex flex-row justify-between text-md font-bold text-black-1 pc:mb-8 pc:mt-[-8px] line-clamp-1">
            {report.departmentName}
          </div>
          <div className="flex flex-row justify-between">
            <div className="flex flex-row items-center justify-center gap-x-4 text-black-2">
              <span>
                <MdWork />
              </span>

              <span className="line-clamp-1 text-base">
                {report.departmentCategory.name}
              </span>
            </div>
            <div className="text-base text-black-2">
              {formatDate(report.publishedAt)}
            </div>
          </div>
        </div>
        <div className="w-full flex h-[68px] flex-col overflow-hidden bg-background-1 p-8 text-sm text-black-1 rounded-16 leading-xl">
          <div
            style={{
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              WebkitLineClamp: 3,
              maxHeight: '2 * 1.2em',
            }}
          >
            {report.excerpt.length > 0 ? report.excerpt : ''}
          </div>
        </div>
      </CommonLink>
      <NoLoginAlertModal opened={opened} onClose={close} />
    </>
  );
};
